import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { RAG as RAG_LEVELS } from 'features/dashboard/charts/scoreColour'
import { PRIVATE_PATHS } from 'utils/paths'
import { fetchNextSteps } from 'services/guidanceService'

const NAV_BY_KEY = {
  tasks_overdue: 'taskManagement',
  tasks_due_week: 'taskManagement',
  reviews_pending: 'taskManagement',
  remedials_review: 'actions'
}

const tint = sev => RAG_LEVELS[sev] || RAG_LEVELS.none

const NextStepsPanel = ({ mode, onStep }) => {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    setLoading(true)
    fetchNextSteps(mode)
      .then(res => { if (live) setData(res?.data?.data || null) })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [mode])

  const act = step => {
    if (step.ctaLabel == null) return
    if (onStep && onStep(step)) return
    const pathKey = NAV_BY_KEY[step.key]
    if (pathKey && PRIVATE_PATHS[pathKey]) navigate(PRIVATE_PATHS[pathKey])
    else if (step.key === 'get_started') navigate(PRIVATE_PATHS.dashboard)
  }

  if (loading) {
    return (
      <Panel>
        <Header><Title>Your next steps</Title></Header>
        <Steps>{Array.from({ length: 3 }).map((_, i) => <SkelRow key={i} />)}</Steps>
      </Panel>
    )
  }

  if (!data || !data.steps?.length) return null

  return (
    <Panel>
      <Header>
        <div>
          <Title>Your next steps</Title>
          <Subtitle>The most important things for you right now.</Subtitle>
        </div>
        {data.personaLabel && <PersonaChip>{data.personaLabel}</PersonaChip>}
      </Header>

      {data.allClear ? (
        <AllClear>
          <CheckCircleRoundedIcon style={{ fontSize: 30, color: RAG_LEVELS.low.fg }} />
          <div>
            <StepTitle>{data.steps[0].title}</StepTitle>
            <StepDesc>{data.steps[0].description}</StepDesc>
          </div>
        </AllClear>
      ) : (
        <Steps>
          {data.steps.map(step => {
            const clickable = step.ctaLabel != null
            return (
              <Step key={step.key} $clickable={clickable} onClick={() => act(step)}>
                <Accent $tone={step.severity} />
                <StepBody>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDesc>{step.description}</StepDesc>
                </StepBody>
                {clickable && (
                  <Cta $tone={step.severity}>
                    <span>{step.ctaLabel}</span>
                    <ArrowForwardRoundedIcon style={{ fontSize: 16 }} />
                  </Cta>
                )}
              </Step>
            )
          })}
        </Steps>
      )}
    </Panel>
  )
}

const Panel = styled.section`
  background: #fff; border: 1px solid #e9ebf0; border-radius: 14px;
  padding: 20px 22px; margin-bottom: 22px;
`
const Header = styled.div`display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 16px;`
const Title = styled.h2`font-size: 18px; font-weight: 800; color: #1b2233; margin: 0;`
const Subtitle = styled.p`font-size: 13.5px; color: #6b7280; margin: 3px 0 0;`
const PersonaChip = styled.span`
  flex: 0 0 auto; font-size: 12px; font-weight: 700; color: #4a5160;
  background: #f0f1f5; padding: 4px 11px; border-radius: 999px; white-space: nowrap;
`
const Steps = styled.div`display: flex; flex-direction: column; gap: 10px;`
const Step = styled.button`
  font: inherit; text-align: left; width: 100%;
  display: flex; align-items: center; gap: 14px; min-height: 56px;
  padding: 12px 14px; border: 1px solid #eceef2; border-radius: 11px; background: #fff;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 130ms ease, border-color 130ms ease;
  &:hover { background: ${({ $clickable }) => ($clickable ? '#fafbfc' : '#fff')}; border-color: ${({ $clickable }) => ($clickable ? '#e0e3ea' : '#eceef2')}; }
`
const Accent = styled.span`
  flex: 0 0 auto; width: 4px; align-self: stretch; border-radius: 3px;
  background: ${({ $tone }) => tint($tone).fg};
`
const StepBody = styled.div`flex: 1 1 auto; min-width: 0;`
const StepTitle = styled.div`font-size: 15.5px; font-weight: 700; color: #1b2233;`
const StepDesc = styled.div`font-size: 13.5px; color: #6b7280; margin-top: 2px; line-height: 1.45;`
const Cta = styled.span`
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap;
  font-size: 13px; font-weight: 700; padding: 8px 13px; border-radius: 9px;
  color: ${({ $tone }) => tint($tone).fg}; background: ${({ $tone }) => tint($tone).bg};
`
const AllClear = styled.div`display: flex; align-items: center; gap: 14px; padding: 8px 4px;`
const SkelRow = styled.div`
  height: 56px; border-radius: 11px; background: #eef0f3;
  @keyframes nsPulse { 0% { opacity: 0.55; } 50% { opacity: 1; } 100% { opacity: 0.55; } }
  animation: nsPulse 1.4s ease-in-out infinite;
`

export default NextStepsPanel
